import { formatUrl } from "@cartographerio/client";
import { Option } from "@cartographerio/fp";
import { point } from "@cartographerio/geometry";
import { Point } from "geojson";
import lodash from "lodash";

export { formatUrl };

export type UrlQuery = Record<string, string>;

export type UrlParts = {
  path?: string;
  fragment?: string;
  query?: UrlQuery;
};

export function locationUrlParts(location: Location): UrlParts {
  const path: string | undefined =
    location.pathname === "" ? undefined : location.pathname;

  const query: UrlQuery | undefined =
    location.search === ""
      ? undefined
      : lodash.fromPairs(
          location.search
            .substring(1)
            .split("&")
            .map(pair => pair.split("=").map(decodeURIComponent))
        );

  const fragment: string | undefined =
    location.hash === "" ? undefined : location.hash.substring(1);

  return { path, fragment, query };
}

export function stringParam(
  query: UrlQuery,
  key: string,
  foldCase: boolean = false
): Option<string> {
  return Option.wrap(query[key]).chain(value =>
    foldCase ? lodash.toLower(lodash.trim(value)) : lodash.trim(value)
  );
}

export function integerParam(query: UrlQuery, key: string): Option<number> {
  return Option.wrap(query[key]).chain(value => parseInt(value, 10));
}

export function numberParam(query: UrlQuery, key: string): Option<number> {
  return Option.wrap(query[key])
    .chain(value => parseFloat(value))
    .filter(num => !isNaN(num));
}

export function booleanParam(query: UrlQuery, key: string): Option<boolean> {
  return Option.wrap(query[key]).chain(
    value =>
      value === "y" || value === "yes" || value === "t" || value === "true"
  );
}

export function arrayParam(
  query: UrlQuery,
  key: string,
  delim: string
): Option<string[]> {
  return stringParam(query, key).flatMap(string =>
    string === "" ? Option.none() : Option.some(string.split(delim))
  );
}

export function pointParam(query: UrlQuery, key: string): Option<Point> {
  function parseNum(str: string): Option<number> {
    const ans = parseFloat(str.trim());
    return isNaN(ans) ? Option.none() : Option.some(ans);
  }

  return stringParam(query, key).flatMap(string => {
    const [lonString, latString] = string.split(",");

    return Option.tupled([
      Option.wrap(lonString).flatMap(parseNum),
      Option.wrap(latString).flatMap(parseNum),
    ]).map(([lon, lat]) => point(lon, lat));
  });
}
